import { Database, LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "./AuthContext";
import { BackupsModal } from "./BackupsModal";
import { useBackupStatus } from "./hooks/useBackupStatus";
import styles from "./Navbar.module.css";
import { useTheme } from "./ThemeProvider";

interface NavbarProps {
	links?: Array<{
		label: string;
		href: string;
		active?: boolean;
		onClick?: (e: React.MouseEvent) => void;
	}>;
	currentApp?: "portfolio" | "blogs" | "notes" | "resume";
}

export const Navbar = (_props: NavbarProps) => {
	const { theme, toggleTheme } = useTheme();
	const { isAuthenticated, logout } = useAuth();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isBackupsOpen, setIsBackupsOpen] = useState(false);

	const { isPending } = useBackupStatus(isAuthenticated);

	const pathname = window.location.pathname;
	const isJournal = pathname === "/" || pathname.startsWith("/blog");
	const isContact = pathname.startsWith("/contact");
	const isNotes = pathname.startsWith("/notes");
	const isResume = pathname.startsWith("/resumes");

	const navLinks = [
		{
			label: "Journal",
			href: "/",
			active: isJournal,
		},
		{
			label: "Contact",
			href: "/contact",
			active: isContact,
		},
	];

	if (isAuthenticated) {
		navLinks.push({
			label: "Notes",
			href: "/notes",
			active: isNotes,
		});
		navLinks.push({
			label: "Resume",
			href: "/resumes",
			active: isResume,
		});
	}

	const handleLinkClick = () => {
		setIsMobileMenuOpen(false);
	};

	return (
		<nav className={styles["nav-bar"]}>
			<div className={styles["nav-container"]}>
				<a href="/" className={styles.logo}>
					adafia<span className={styles["text-accent"]}>_</span>
				</a>

				<ul
					className={`${styles["nav-links"]} ${isMobileMenuOpen ? styles["mobile-open"] : ""}`}
				>
					{navLinks.map((link) => (
						<li key={link.label}>
							<a
								href={link.href}
								onClick={handleLinkClick}
								className={`${styles["nav-link"]} ${link.active ? styles.active : ""}`}
							>
								{link.label}
							</a>
						</li>
					))}
				</ul>

				<div className={styles["nav-actions"]}>
					{isAuthenticated && (
						<button
							type="button"
							className={styles["switcher-btn"]}
							onClick={() => setIsBackupsOpen(true)}
							title="Backup & Restore Management"
							aria-label="Backup & Restore Management"
						>
							<Database
								size={16}
								className={isPending ? styles["animate-pulse"] : ""}
							/>
							<span className={styles["hidden-mobile"]}>Backups</span>
						</button>
					)}

					<button
						type="button"
						onClick={toggleTheme}
						className={`${styles["switcher-btn"]} ${styles["theme-toggle"]}`}
						aria-label="Toggle theme"
					>
						{theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
					</button>

					{isAuthenticated && (
						<button
							type="button"
							onClick={logout}
							className={`${styles["switcher-btn"]} ${styles["logout-btn"]}`}
							aria-label="Log out"
							title="Log out"
						>
							<LogOut size={16} />
						</button>
					)}

					<button
						type="button"
						className={`${styles["switcher-btn"]} ${styles["mobile-menu-toggle"]}`}
						onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						aria-label="Toggle mobile menu"
						aria-expanded={isMobileMenuOpen}
					>
						{isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
					</button>
				</div>
			</div>
			<BackupsModal
				isOpen={isBackupsOpen}
				onClose={() => setIsBackupsOpen(false)}
			/>
		</nav>
	);
};
